import {
  Box,
  Grid,
  Toolbar,
  Typography,
  CircularProgress,
  Button,
  Pagination,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../../utils/Api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { CloudDownloadOutlined, Visibility, Close, KeyboardArrowLeftOutlined } from "@mui/icons-material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

function DamageReport() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

  const [loading, setLoading] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(new Date());

  // Search
  const [searchText, setSearchText] = useState("");

  // Data
  const [damageRows, setDamageRows] = useState([]); // dbResponse array
  const [damageReportData, setDamageReportData] = useState(null);

  // Detail Modal
  const [openModal, setOpenModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const handleOpenModal = (entry) => {
    setSelectedEntry(entry);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedEntry(null);
  };

  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);

  // Delete loader
  const [deletingId, setDeletingId] = useState(null);

  // Delete Confirm Modal
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  const openDeleteDialog = (entry) => {
    if (!entry?._id) return;
    setDeleteCandidate(entry);
    setOpenDeleteConfirm(true);
  };

  const closeDeleteDialog = () => {
    if (deletingId && deleteCandidate?._id === deletingId) return;
    setOpenDeleteConfirm(false);
    setDeleteCandidate(null);
  };

  const getDamageReport = async (download = false) => {
    if (!outletData?._id) return;

    setLoading(true);
    try {
      const date = format(selectedStartDate, "yyyy-MM-dd");

      const params = {
        startDate: date,
        endDate: date,
        outlet: outletData._id,
      };

      if (download) params.isDownload = "true";

      const response = await axios.get(`${API.BASE_URL}damaged-product-entry`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: download ? "blob" : "json",
      });

      if (download) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        const selectedOutletName = outletData?.name || outletData?.outletName || "Outlet";
        const fileName = `${selectedOutletName} Damage Report (${date}).xlsx`;

        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);

        toast.success("Report Excel file downloaded successfully!", {
          position: "bottom-center",
          autoClose: 3000,
        });
        return;
      }

      const rows = response?.data?.data?.dbResponse || [];
      setDamageReportData(response?.data?.data || null);
      setDamageRows(Array.isArray(rows) ? rows : []);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error:", error);
      toast.error(download ? "Failed to download report" : "Failed to fetch report", {
        autoClose: 3000,
      });
      if (!download) setDamageRows([]);
    } finally {
      setLoading(false);
    }
  };

  const performDeleteDamageEntry = async (entry) => {
    const id = entry?._id;
    if (!id) return;

    setDeletingId(id);
    try {
      await axios.delete(`${API.BASE_URL}damaged-product-entry`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { "_id": id },
      });
      setDamageRows((prev) => (Array.isArray(prev) ? prev.filter((x) => x?._id !== id) : prev));

      if (selectedEntry?._id === id) handleCloseModal();
      getDamageReport();
      toast.success("Entry deleted successfully!", { autoClose: 2500 });
      closeDeleteDialog();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete entry", { autoClose: 3000 });
    } finally {
      setDeletingId(null);
    }
  };

  const filteredRows = useMemo(() => {
    const rows = Array.isArray(damageRows) ? damageRows : [];
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const productName = (r?.product?.name || "").toLowerCase();
      const remark = (r?.remark || "").toLowerCase();
      const outletName = (r?.outlet?.name || "").toLowerCase();

      const addonText = (r?.addons || [])
        .map((a) => `${a?.addonTitle || ""} ${a?.addonValueTitle || ""}`.trim())
        .join(" ")
        .toLowerCase();

      const dateStr = r?.date ? String(r.date).toLowerCase() : "";

      return (
        productName.includes(q) ||
        remark.includes(q) ||
        outletName.includes(q) ||
        addonText.includes(q) ||
        dateStr.includes(q)
      );
    });
  }, [damageRows, searchText]);

  const legendTotals = useMemo(() => {
    const rows = Array.isArray(filteredRows) ? filteredRows : [];
    return rows.reduce(
      (acc, r) => {
        const qty = Number(r?.quantity || 0);
        const price = Number(r?.product?.offerPrice ?? r?.product?.mrp ?? 0);
        acc.totalEntries += 1;
        acc.totalQty += qty;
        acc.totalValue += qty * price;
        return acc;
      },
      { totalEntries: 0, totalQty: 0, totalValue: 0 }
    );
  }, [filteredRows]);

  const totalPages = Math.ceil((filteredRows?.length || 0) / itemsPerPage) || 1;
  const paginatedRows = filteredRows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    getDamageReport(false);
  }, []);

  const candidateName =
    deleteCandidate?.product?.name ||
    deleteCandidate?.productName ||
    deleteCandidate?._id ||
    "this entry";

  return (
    <Box className="dashBoard" sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
      <Toolbar />

      <Grid container justifyContent="center" sx={{ pb: 6 }}>
        <Grid item xs={12} md={11}>
          {/* Header */}
          <Card
            elevation={0}
            sx={{
              mt: 4,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Stack flexDirection={'row'}>
                  <KeyboardArrowLeftOutlined sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)} fontSize="large" />
                  <Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                      Damaged Product Entry Report
                    </Typography>
                    <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                      Select date, search entries, view details, download Excel, or delete entries.
                    </Typography>
                  </Box>
                </Stack>

                <Chip
                  icon={<FilterAltOutlined />}
                  label={`Records: ${filteredRows?.length || 0}`}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                  variant="outlined"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "#fff",
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="center" justifyContent={"space-between"}>
                <Grid item xs={12} md={3} sx={{ zIndex: 1000 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Date
                  </Typography>

                  <DatePicker
                    selected={selectedStartDate}
                    onChange={(date) => {
                      setSelectedStartDate(date);
                      setCurrentPage(1);
                    }}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={
                      <TextField
                        fullWidth
                        value={selectedStartDate ? format(selectedStartDate, "yyyy-MM-dd") : ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52, background: "#fff" },
                        }}
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12} md={5}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Search Entry
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Search by product / addon / outlet / remark"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      setCurrentPage(1);
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52, background: "#fff" } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8, textAlign: "center" }}>
                    Actions
                  </Typography>

                  <Stack direction="row" justifyContent={"space-between"} spacing={1}>
                    <Button
                      variant="contained"
                      disabled={loading}
                      onClick={() => getDamageReport(false)}
                      sx={{
                        height: 52,
                        width: "70%",
                        borderRadius: 2,
                        fontWeight: 800,
                        textTransform: "none",
                        backgroundColor: "#003049",
                        "&:hover": { backgroundColor: "#00263a" },
                      }}
                    >
                      {loading ? "Loading..." : "Get"}
                    </Button>

                    <CloudDownloadOutlined
                      onClick={() => filteredRows?.length > 0 && getDamageReport(true)}
                      sx={{
                        fontSize: "35px",
                        cursor: filteredRows?.length > 0 ? "pointer" : "not-allowed",
                        color: filteredRows?.length > 0 ? "#003049" : "#94a3b8",
                        display: "flex",
                        alignSelf: "center",
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Table */}
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  <CircularProgress />
                  <Typography sx={{ color: "#334155", fontWeight: 700 }}>Fetching Damage Entries...</Typography>
                </Box>
              ) : filteredRows?.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                    No records to display
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Select a date and click “Get” to view results.
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Summary chips */}
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#fff" }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip
                          icon={<FilterAltOutlined />}
                          label={`Rows: ${legendTotals.totalEntries}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />
                        <Chip
                          label={`Total Qty: ${legendTotals.totalQty}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />
                      </Stack>

                      <Chip
                        label={`Total Value: ₹${legendTotals.totalValue.toFixed(2)}`}
                        sx={{
                          fontWeight: 900,
                          borderRadius: 2,
                          backgroundColor: "#003049",
                          color: "#fff",
                          "& .MuiChip-label": { color: "#fff" },
                        }}
                      />
                    </Stack>
                  </Box>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ borderRadius: 0, maxHeight: "70vh", overflow: "auto" }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {["Date", "Product", "Qty", "Addons", "Value", "Remark", "Detail", "Delete"].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 900,
                                color: "#0f172a",
                                background: "#f1f5f9",
                                borderBottom: "1px solid #e2e8f0",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                              }}
                              align={["Product", "Remark"].includes(h) ? "left" : "center"}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {paginatedRows.map((row, index) => {
                          const dateStr = row?.date ? new Date(row.date).toISOString().split("T")[0] : "N/A";
                          const productName = row?.product?.name || "N/A";
                          const qty = Number(row?.quantity || 0);

                          const offerPrice = Number(row?.product?.offerPrice ?? row?.product?.mrp ?? 0);
                          const value = qty * offerPrice;

                          const addons = row?.addons || [];
                          const addonPreview =
                            addons.length === 0
                              ? "—"
                              : addons
                                .slice(0, 2)
                                .map((a) => `${a?.addonTitle || ""}: ${a?.addonValueTitle || ""}`.trim())
                                .join(", ");

                          const isDeletingThis = deletingId === row?._id;

                          return (
                            <TableRow
                              key={(row?._id || index) + "-" + index}
                              hover
                              sx={{
                                backgroundColor: index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                "& td": { borderBottom: "1px solid #f1f5f9" },
                              }}
                            >
                              <TableCell align="center" sx={{ fontWeight: 800, color: "#0f172a" }}>
                                {dateStr}
                              </TableCell>

                              <TableCell align="left">
                                <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#0f172a" }}>
                                  {productName}
                                </Typography>
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 900, color: "#0f172a" }}>
                                {qty}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 700 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 800 }}>{addonPreview}</Typography>
                                {addons.length > 2 && (
                                  <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                                    +{addons.length - 2} more
                                  </Typography>
                                )}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 900, color: "#0f172a" }}>
                                ₹{value.toFixed(2)}
                              </TableCell>

                              <TableCell align="left" sx={{ maxWidth: 420 }}>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: "#334155",
                                    fontWeight: 700,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {row?.remark || "—"}
                                </Typography>
                              </TableCell>

                              <TableCell align="center">
                                <IconButton onClick={() => handleOpenModal(row)}>
                                  <Visibility />
                                </IconButton>
                              </TableCell>

                              <TableCell align="center">
                                <IconButton
                                  onClick={() => openDeleteDialog(row)}
                                  disabled={isDeletingThis}
                                  sx={{
                                    color: isDeletingThis ? "#94a3b8" : "#dc2626",
                                  }}
                                >
                                  {isDeletingThis ? <CircularProgress size={18} /> : <DeleteOutline />}
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider />
                  <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detail Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 900,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            position: "relative",
          }}
        >
          <Box sx={{ flex: 1, textAlign: "center" }}>Damage Entry Details</Box>

          <Stack direction="row" spacing={1} sx={{ position: "absolute", right: 10, top: 10 }}>
            <IconButton onClick={handleCloseModal} disabled={deletingId === selectedEntry?._id}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {selectedEntry ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Date:</strong>{" "}
                  {selectedEntry?.date ? new Date(selectedEntry.date).toISOString().split("T")[0] : "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Quantity:</strong> {Number(selectedEntry?.quantity || 0)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 1 }}>
                  Product
                </Typography>
                <Box sx={{ mt: 1, p: 1.5, border: "1px solid #e2e8f0", borderRadius: 2 }}>
                  <Typography>
                    <strong>Name:</strong> {selectedEntry?.product?.name || "N/A"}
                  </Typography>
                  <Typography>
                    <strong>Food Type:</strong> {selectedEntry?.product?.foodType || "N/A"}{" "}
                    <strong style={{ marginLeft: 16 }}>Class:</strong> {selectedEntry?.product?.classType || "N/A"}
                  </Typography>
                  <Typography>
                    <strong>Offer Price:</strong> ₹{Number(selectedEntry?.product?.offerPrice ?? 0).toFixed(2)}{" "}
                    <strong style={{ marginLeft: 16 }}>MRP:</strong> ₹{Number(selectedEntry?.product?.mrp ?? 0).toFixed(2)}
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: "#475569" }}>
                    {selectedEntry?.product?.description || ""}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 1 }}>
                  Outlet
                </Typography>
                <Box sx={{ mt: 1, p: 1.5, border: "1px solid #e2e8f0", borderRadius: 2 }}>
                  <Typography>
                    <strong>Name:</strong> {selectedEntry?.outlet?.name || "N/A"}
                  </Typography>
                  <Typography>
                    <strong>City/State:</strong> {selectedEntry?.outlet?.city || "N/A"} / {selectedEntry?.outlet?.state || "N/A"}
                  </Typography>
                  <Typography>
                    <strong>Phone:</strong> {selectedEntry?.outlet?.phone || "N/A"}{" "}
                    <strong style={{ marginLeft: 16 }}>Email:</strong> {selectedEntry?.outlet?.email || "N/A"}
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: "#475569" }}>
                    <strong>Address:</strong> {selectedEntry?.outlet?.address || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 1 }}>
                  Addons
                </Typography>

                {(selectedEntry?.addons || []).length === 0 ? (
                  <Typography sx={{ color: "#64748b", mt: 0.5 }}>No addons</Typography>
                ) : (
                  <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", mt: 1 }}>
                    <thead>
                      <tr>
                        <th style={{ padding: "10px", border: "1px solid #ccc", textAlign: "left" }}>Addon Title</th>
                        <th style={{ padding: "10px", border: "1px solid #ccc", textAlign: "left" }}>Addon Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEntry.addons.map((a, i) => (
                        <tr key={i}>
                          <td style={{ padding: "10px", border: "1px solid #ccc" }}>{a?.addonTitle || "N/A"}</td>
                          <td style={{ padding: "10px", border: "1px solid #ccc" }}>{a?.addonValueTitle || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 1 }}>
                  Remark
                </Typography>
                <Box sx={{ mt: 1, p: 1.5, border: "1px solid #e2e8f0", borderRadius: 2, background: "#f8fafc" }}>
                  <Typography sx={{ color: "#0f172a", fontWeight: 700 }}>
                    {selectedEntry?.remark || "—"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  <strong>Entry Value:</strong>{" "}
                  ₹
                  {(
                    Number(selectedEntry?.quantity || 0) *
                    Number(selectedEntry?.product?.offerPrice ?? selectedEntry?.product?.mrp ?? 0)
                  ).toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography>No details available</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Modal */}
      <Dialog open={openDeleteConfirm} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 900,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          Confirm Delete
          <IconButton onClick={closeDeleteDialog} disabled={deletingId === deleteCandidate?._id}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>
            Are you sure you want to delete this damaged entry?
          </Typography>

          <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
            <Typography sx={{ fontSize: 13, color: "#334155", fontWeight: 800 }}>
              Product: {candidateName}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
              This action cannot be undone.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={closeDeleteDialog}
              disabled={deletingId === deleteCandidate?._id}
              sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={() => performDeleteDamageEntry(deleteCandidate)}
              disabled={!deleteCandidate?._id || deletingId === deleteCandidate?._id}
              startIcon={
                deletingId === deleteCandidate?._id ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <DeleteOutline />
              }
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                textTransform: "none",
                backgroundColor: "#dc2626",
                "&:hover": { backgroundColor: "#b91c1c" },
              }}
            >
              {deletingId === deleteCandidate?._id ? "Deleting..." : "Delete"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default DamageReport;